import type { JSX } from 'react'

export interface ThresholdVisualizationProps {
  /** Total SLA time in minutes. `null` means the rule applies to all SLAs. */
  slaMinutes: number | null
  /** Threshold position along the SLA timeline, 1-100. */
  thresholdPct: number
  /** Which SLA timeline the threshold sits on. */
  triggerType: 'first_response_breach' | 'resolution_breach'
}

const END_LABELS: Record<ThresholdVisualizationProps['triggerType'], string> = {
  first_response_breach: 'First response breach',
  resolution_breach: 'Resolution breach',
}

/** Clamp a percentage into the 1-100 range so the marker can't escape the bar. */
function clampPct(pct: number): number {
  if (!Number.isFinite(pct)) return 1
  return Math.min(100, Math.max(1, pct))
}

/**
 * Pure presentational component: a horizontal bar showing the SLA timeline
 * from "Conversation opened" to the breach point, with a marker at
 * `thresholdPct`% indicating where an escalation fires.
 *
 * When `slaMinutes` is provided, the computed elapsed time at the threshold
 * (`thresholdPct% × slaMinutes`) is shown. When it is `null`, a
 * "Applies to all SLAs" placeholder is shown instead.
 */
export default function ThresholdVisualization({
  slaMinutes,
  thresholdPct,
  triggerType,
}: ThresholdVisualizationProps): JSX.Element {
  const pct = clampPct(thresholdPct)
  const endLabel = END_LABELS[triggerType]
  const computedMinutes =
    slaMinutes != null ? Math.round((pct / 100) * slaMinutes) : null

  return (
    <div className="threshold-viz flex flex-col gap-1 text-xs">
      {/* Endpoint labels */}
      <div className="flex justify-between text-gray-500">
        <span>Conversation opened</span>
        <span>{endLabel}</span>
      </div>

      {/* The timeline bar */}
      <div
        className="relative h-2 rounded bg-gray-200"
        role="img"
        aria-label={`Escalation threshold at ${pct}% of the ${endLabel.toLowerCase()} SLA timeline`}
      >
        {/* Colored zone up to the threshold */}
        <div
          className="absolute left-0 top-0 h-full rounded-l bg-amber-300"
          style={{ width: `${pct}%` }}
          data-testid="threshold-zone"
        />
        {/* Marker at the threshold position */}
        <div
          className="absolute top-1/2 h-3 w-0.5 -translate-x-1/2 -translate-y-1/2 bg-amber-600"
          style={{ left: `${pct}%` }}
          data-testid="threshold-marker"
        />
      </div>

      {/* Marker caption */}
      <div className="text-amber-700">
        <span className="font-medium">{pct}%</span>{' '}
        <span className="text-gray-500">escalates here</span>
        {computedMinutes != null && (
          <span className="text-gray-500"> (~{computedMinutes} min)</span>
        )}
      </div>

      {/* Placeholder when there is no concrete SLA time */}
      {slaMinutes == null && (
        <div className="text-gray-500">Applies to all SLAs</div>
      )}
    </div>
  )
}
